import { LightningElement, wire } from "lwc";
import { ResizeAdapter } from "common/resizeAdapter";

export class Particle {
  private w = 0;
  private h = 0;
  private x = 0;
  private y = 0;
  private vx = 0;
  private vy = 0;
  private alpha = 0;
  private color = "";
  // Canvas dimensions need to be accessible for overrides + a default size for initialization
  canvasWidth = 2000;
  canvasHeight = 2000;

  constructor(xPoint: number, yPoint: number, color: string) {
    this.w = this.h = Math.random() * 4 + 1;
    this.x = xPoint - this.w / 2;
    this.y = yPoint - this.h / 2;
    this.vx = (Math.random() - 0.5) * 10;
    this.vy = (Math.random() - 0.5) * 10;
    this.alpha = Math.random() * 0.5 + 0.5;
    this.color = color;

    const vy_limit = Math.sqrt(25 - this.vx * this.vx);
    if (Math.abs(this.vy) > vy_limit) {
      this.vy = this.vy > 0 ? vy_limit : -vy_limit;
    }
  }

  gravity = 0.05;

  move() {
    this.x += this.vx;
    this.vy += this.gravity;
    this.y += this.vy;
    this.alpha -= 0.0002; // update alpha to handle trail lengths/longevity of firework

    if (
      this.x <= -this.w ||
      this.x >= this.canvasWidth ||
      this.y >= this.canvasHeight ||
      this.alpha <= 0
    ) {
      return false;
    }
    return true;
  }

  draw(ctx: CanvasRenderingContext2D) {
    ctx.save();
    ctx.beginPath();
    ctx.translate(this.x + this.w / 2, this.y + this.h / 2);
    ctx.arc(0, 0, this.w, 0, Math.PI * 2);
    ctx.fillStyle = this.color;
    ctx.globalAlpha = this.alpha;
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
}

export default class Fireworks extends LightningElement {
  private particles: Particle[] = [];
  private probability = 0.03; // update this value to increase or decrease the amount of fireworks shown
  private w = 0;
  private h = 0;
  private ctx: CanvasRenderingContext2D | null = null;
  private rafId: number | null = null;
  private resizeHandler: (() => void) | null = null;
  private hasRendered = false;
  private timeoutId: number | null = null;
  private durationTimeoutId: number | null = null;
  private isFadingOut = false;
  private startDelay = 1500;
  private durationLimit = 8000;

  // Colors defined by branding (3rd + 5th rows): https://zeroheight.com/7dcc358f4/p/28adf7-color/b/786d2f/i/261434347
  private colorPalette = [
    "#FEB9A5", // hot orange (row 3)
    "#FF784F", // darker vibrant orange (row 5)
    "#FFBA90", // vibrant orange (row 3)
    "#F38303", // pumpkin orange (row 5)
    "#D79304", // gold (row 5)
    "#41B658", // green (row 5)
    "#03B4A7", // turquoise (row 5)
    "#57A3FD", // cornflower blue (row 5)
    "#8E9BEF", // indigo (row 5)
    "#B78DEF", // darker vibrant purple (row 5)
    "#FE7765", // salmon (row 5)
    "#FE7298", // hot pink (row 3)
    "#FCC003", // yellow (row 3)
    "#91DB8B", // light Green (row 3)
    "#04E1CB", // light turquoise (row 3)
    "#AACBFF", // light purple (row 3)
    "#BEC7F6", // vibrant indigo (row 3)
    "#D7BFF2", // vibrant purple (row 3)
    "#E5B9FE", // vibrant violet (row 3)
    "#FDB6C5", // vibrant pink (row 3)
    "#FEB8AB", // vibrant red (row 3)
  ];

  @wire(ResizeAdapter)
  private onResize() {
    this.resizeHandler?.();
  }

  renderedCallback() {
    if (this.hasRendered) {
      return;
    }

    if (this.ctx) return;

    const canvas = this.template.querySelector(
      ".fireworks-canvas"
    ) as HTMLCanvasElement;

    if (canvas) {
      this.ctx = canvas.getContext("2d");
      this.resizeCanvas(canvas);

      this.resizeHandler = () => this.resizeCanvas(canvas);
      this.timeoutId = window.setTimeout(() => {
        this.updateWorld();

        this.durationTimeoutId = window.setTimeout(() => {
          this.stopAnimation();
        }, this.durationLimit);
      }, this.startDelay);

      this.hasRendered = true;
    }
  }

  disconnectedCallback() {
    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
    }
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId);
    }
    if (this.durationTimeoutId) {
      window.clearTimeout(this.durationTimeoutId);
    }
  }

  private resizeCanvas(canvas: HTMLCanvasElement) {
    if (canvas) {
      this.w = canvas.width = window.innerWidth;
      this.h = canvas.height = window.innerHeight;
      Particle.prototype.canvasWidth = this.w;
      Particle.prototype.canvasHeight = this.h;
    }
  }

  private updateWorld = () => {
    this.update();
    this.paint();
    this.rafId = window.requestAnimationFrame(this.updateWorld);
  };

  private update() {
    if (
      !this.isFadingOut &&
      this.particles.length < 500 &&
      Math.random() < this.probability
    ) {
      this.createFirework();
    }

    const alive: Particle[] = [];
    for (let i = 0; i < this.particles.length; i++) {
      if (this.particles[i].move()) {
        alive.push(this.particles[i]);
      }
    }
    this.particles = alive;
  }

  private paint() {
    const ctx = this.ctx;
    if (!ctx) return;

    ctx.globalCompositeOperation = "source-over";
    ctx.clearRect(0, 0, this.w, this.h);

    ctx.fillStyle = "rgba(0, 0, 0, 0)";
    ctx.fillRect(0, 0, this.w, this.h);

    ctx.globalCompositeOperation = "lighter";
    for (let i = 0; i < this.particles.length; i++) {
      this.particles[i].draw(ctx);
    }
  }

  private createFirework() {
    const xPoint = Math.random() * (this.w - 200) + 100;
    const yPoint = Math.random() * (this.h - 200) + 100;
    const nFire = Math.random() * 50 + 100;
    const randomIndex = Math.floor(Math.random() * this.colorPalette.length);
    const c = this.colorPalette[randomIndex];

    for (let i = 0; i < nFire; i++) {
      const particle = new Particle(xPoint, yPoint, c);
      this.particles.push(particle);
    }
  }

  private stopAnimation() {
    this.isFadingOut = true;

    window.setTimeout(() => {
      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      this.ctx?.clearRect(0, 0, this.w, this.h);
    }, 2500);
  }
}
